import { useEffect, useRef, useState } from "react";
import { FolderOpen, Building2, AlertTriangle, Trash2, FolderInput, ScanText, X, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { fetchDocuments, uploadDocument, deleteDocument, getDownloadUrl, extraireDocumentAsync, extractionStatut, toggleAnalyseDocument, setRemarquesDocument, sortDocuments, detectYear, extraireDateJugement, dateJugementStatut, renameDocument, moveDocuments, deleteDocuments, TYPES_DOCUMENT } from "@/lib/dataroom";
import { isRestructuration } from "@/lib/checklist";
import { Landmark, Scale, Paperclip, Gavel } from "lucide-react";
import UploadZone from "@/components/dataroom/UploadZone";
import DocumentRow from "@/components/dataroom/DocumentRow";

const AUTO_EXTRACT = /\.(pdf|csv|txt|xlsx|xlsm|xls)$/i;
const COMPTABLE = new Set(["fec", "liasse_fiscale", "balance_clients", "balance_fournisseurs", "tableau_previsionnel_client", "tableau_amortissements", "valorisation_immobilisations", "tableau_stocks", "balance", "grand_livre", "attestations_fiscales_sociales", "tableau_amortissement_pret"]);
const BANQUE = new Set(["rib", "releve_bancaire"]);
const ADMIN_DCP = new Set(["identite_gerant", "kbis", "etat_privileges", "fiches_paie"]);
const JURIDIQUE_RE = /statut|k-?bis|kbis|\bag\b|assembl|pv\b|proc[èe]s|pacte|bail|contrat|jugement|greffe|juridique/i;
// SUPER-HOTFIX 132 — Cloisonnement : les pièces de la section « Demandes de virements » (CASH RJ)
// ne doivent PAS polluer la Dataroom principale ; visibles uniquement depuis l'onglet CASH RJ.
const CASH_RJ_TYPES = new Set(["facture", "devis", "bon_commande", "justificatif_virement"]);
const COMPTABLE_RE = /fec|liasse|bilan|balance|grand.?livre|stock|amortis|immo|relev|compte|plaquette|dsn|paie|salaire/i;
export const PV_CATEGORY = { key: "pv_jugement", title: "PV et Jugements", icon: Gavel, empty: "Jugements (ouverture, renouvellement…) et procès-verbaux. La date de l'acte est lue automatiquement par l'IA et affichée à côté du fichier." };
export const CATEGORIES = [
    { key: "administratif", title: "1. Documents administratifs & dirigeant", icon: Building2, empty: "Pièce d'identité du gérant, K-Bis (<3 mois), état des privilèges & nantissements, fiches de paie…" },
    { key: "banques", title: "2. Banques", icon: Landmark, empty: "RIB et relevés bancaires de chaque compte de l'entreprise." },
    { key: "comptable", title: "3. Documents comptables", icon: Landmark, empty: "Liasses, FEC, balances âgées, stocks, amortissements, immobilisations…" },
    { key: "juridique", title: "4. Documents juridiques", icon: Scale, empty: "Statuts, K-Bis, PV d'AG, pactes, baux…" },
    { key: "autres", title: "5. Autres pièces", icon: Paperclip, empty: "Pièces diverses — indiquez l'année dans la période." },
];
export function categoriesFor(isResto) {
    return isResto ? [PV_CATEGORY, ...CATEGORIES] : CATEGORIES;
}
export function categorize(d) {
    if (d.type_document === "pv_jugement") return "pv_jugement";
    if (BANQUE.has(d.type_document)) return "banques";
    if (ADMIN_DCP.has(d.type_document)) return "administratif";
    if (COMPTABLE.has(d.type_document)) return "comptable";
    const label = `${d.type_personnalise || ""} ${d.nom_fichier || ""}`;
    if (JURIDIQUE_RE.test(label)) return "juridique";
    if (COMPTABLE_RE.test(label)) return "comptable";
    return "autres";
}
const POLL_MS = 4000;

export default function DataroomSection({ dossierId, group, contexteAudit, onCountChange, onDocsChange }) {
    const [docs, setDocs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [extracting, setExtracting] = useState({});
    const [datePending, setDatePending] = useState({});
    const pendingRef = useRef(new Set());
    const dateRef = useRef(new Set());
    const isResto = isRestructuration(contexteAudit);
    // SUPER-LOT 103 — sélection multiple & actions de masse
    const [selected, setSelected] = useState(() => new Set());
    const [moveType, setMoveType] = useState("");
    const [bulkProgress, setBulkProgress] = useState(null);
    const bulkRef = useRef(null);
    const toggleSelect = (id) => setSelected((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
    function _bulkTick(id) {
        const b = bulkRef.current;
        if (!b || !b.ids.has(id)) return;
        b.ids.delete(id); b.done += 1;
        setBulkProgress({ done: b.done, total: b.total });
        if (b.ids.size === 0) { bulkRef.current = null; toast.success("Extraction IA de masse terminée", { description: `${b.total} fichier(s) traité(s)` }); setTimeout(() => setBulkProgress(null), 4000); }
    }

    useEffect(() => {
        fetchDocuments(dossierId)
            .then(setDocs)
            .catch((e) => setError(e.message))
            .finally(() => setLoading(false));
    }, [dossierId]);

    useEffect(() => {
        const visible = docs.filter((d) => !CASH_RJ_TYPES.has(d.type_document));
        onCountChange?.(visible.length);
        if (!loading) onDocsChange?.(docs);
    }, [docs, loading, onCountChange, onDocsChange]);

    useEffect(() => {
        const timer = setInterval(async () => {
            const ids = Array.from(pendingRef.current);
            if (!ids.length) return;
            for (const id of ids) {
                try {
                    const s = await extractionStatut(id);
                    if (s.state === "done") {
                        pendingRef.current.delete(id);
                        _bulkTick(id);
                        setExtracting((x) => ({ ...x, [id]: false }));
                        setDocs((d) => d.map((x) => (x.id === id ? { ...x, contenu_extrait: "ok" } : x)));
                        toast.success("Texte extrait (arrière-plan)", { description: `${s.chars?.toLocaleString("fr-FR") ?? ""} caractères` });
                    } else if (s.state === "error" || s.state === "unknown") {
                        pendingRef.current.delete(id);
                        _bulkTick(id);
                        setExtracting((x) => ({ ...x, [id]: false }));
                        if (s.state === "error") toast.error("Extraction automatique échouée", { description: `${s.detail || ""} — utilisez « Extraire le texte ».` });
                    }
                } catch { /* réessaie au prochain tick */ }
            }
            for (const id of Array.from(dateRef.current)) {
                try {
                    const s = await dateJugementStatut(id);
                    if (s.state === "done") {
                        dateRef.current.delete(id);
                        setDatePending((x) => ({ ...x, [id]: false }));
                        setDocs((d) => d.map((x) => (x.id === id ? { ...x, remarques: s.remarques } : x)));
                        if (s.remarques) toast.success("Date lue automatiquement", { description: s.remarques });
                    } else if (s.state === "error" || s.state === "unknown") {
                        dateRef.current.delete(id);
                        setDatePending((x) => ({ ...x, [id]: false }));
                        if (s.state === "error") toast.error("Lecture de la date échouée", { description: s.detail || "Renseignez la date manuellement." });
                    }
                } catch { /* réessaie au prochain tick */ }
            }
        }, POLL_MS);
        return () => clearInterval(timer);
    }, []);

    async function autoExtract(doc) {
        if (!AUTO_EXTRACT.test(doc.nom_fichier || "")) return;
        try {
            await extraireDocumentAsync(doc.id);
            pendingRef.current.add(doc.id);
            setExtracting((x) => ({ ...x, [doc.id]: true }));
        } catch { /* extraction manuelle reste disponible */ }
    }

    async function autoDate(doc) {
        try {
            await extraireDateJugement(doc.id);
            dateRef.current.add(doc.id);
            setDatePending((x) => ({ ...x, [doc.id]: true }));
        } catch { /* la remarque reste éditable manuellement */ }
    }

    async function handleUpload(files, type, periode, typePersonnalise, estHolding) {
        for (const file of files) {
            try {
                const detected = periode ? "" : detectYear(file.name);
                const doc = await uploadDocument(dossierId, file, type, periode || detected, typePersonnalise, estHolding);
                setDocs((d) => [doc, ...d]);
                toast.success("Document déposé", { description: detected ? `${file.name} — année ${detected} détectée depuis le nom du fichier` : file.name });
                if (type === "pv_jugement") autoDate(doc);
                else autoExtract(doc);
            } catch (e) {
                toast.error(`Échec : ${file.name}`, {
                    description: /bucket not found/i.test(e.message)
                        ? "Bucket « dataroom » introuvable — exécutez supabase_storage.sql dans Supabase."
                        : e.message,
                });
            }
        }
    }

    async function handleDownload(doc) {
        try {
            window.open(await getDownloadUrl(doc.url_stockage), "_blank", "noopener,noreferrer");
        } catch (e) {
            toast.error("Téléchargement impossible", { description: e.message });
        }
    }

    async function handleExtract(doc) {
        setExtracting((s) => ({ ...s, [doc.id]: true }));
        try {
            await extraireDocumentAsync(doc.id);
            pendingRef.current.add(doc.id);
            toast.info("Extraction lancée en arrière-plan", { description: `${doc.nom_fichier} — le statut se met à jour automatiquement` });
        } catch (e) {
            setExtracting((s) => ({ ...s, [doc.id]: false }));
            toast.error("Extraction impossible", { description: e.message });
        }
    }

    async function handleToggleAnalyse(doc) {
        try {
            const res = await toggleAnalyseDocument(doc.id);
            setDocs((d) => d.map((x) => (x.id === doc.id ? { ...x, inclure_analyse: res.inclure_analyse } : x)));
            toast.success(res.inclure_analyse ? "Document inclus dans l'analyse IA" : "Document exclu de l'analyse IA", { description: doc.nom_fichier });
        } catch (e) {
            toast.error("Modification impossible", { description: e.message });
        }
    }

    async function handleDelete(doc) {
        try {
            await deleteDocument(doc);
            setDocs((d) => d.filter((x) => x.id !== doc.id));
            toast.success("Document supprimé");
        } catch (e) {
            toast.error("Suppression impossible", { description: e.message });
        }
    }

    async function handleRemarques(doc, remarques) {
        try {
            const r = await setRemarquesDocument(doc.id, remarques);
            setDocs((d) => d.map((x) => (x.id === doc.id ? { ...x, remarques: r.remarques } : x)));
        } catch (e) {
            toast.error("Remarque non enregistrée", { description: e.message });
        }
    }

    async function handleRename(doc, nom) {
        try {
            const clean = await renameDocument(doc.id, nom);
            setDocs((d) => d.map((x) => (x.id === doc.id ? { ...x, nom_fichier: clean } : x)));
            toast.success("Fichier renommé", { description: clean });
        } catch (e) {
            toast.error("Renommage impossible", { description: e.message });
        }
    }

    async function bulkMove() {
        const ids = [...selected];
        try {
            await moveDocuments(ids, moveType);
            setDocs((d) => d.map((x) => (selected.has(x.id) ? { ...x, type_document: moveType } : x)));
            toast.success(`${ids.length} document(s) déplacé(s)`);
            setSelected(new Set()); setMoveType("");
        } catch (e) { toast.error("Déplacement impossible", { description: e.message }); }
    }

    async function bulkDelete() {
        const chosen = docs.filter((d) => selected.has(d.id));
        if (!window.confirm(`Supprimer DÉFINITIVEMENT ${chosen.length} fichier(s) ?\n\nSuppression du Storage Supabase ET de la base — action irréversible.`)) return;
        try {
            await deleteDocuments(chosen);
            const ids = new Set(chosen.map((d) => d.id));
            setDocs((d) => d.filter((x) => !ids.has(x.id)));
            toast.success(`${chosen.length} fichier(s) supprimé(s)`);
            setSelected(new Set());
        } catch (e) { toast.error("Suppression impossible", { description: e.message }); }
    }

    async function bulkExtract() {
        const chosen = docs.filter((d) => selected.has(d.id) && AUTO_EXTRACT.test(d.nom_fichier || ""));
        if (!chosen.length) { toast.info("Aucun fichier extractible sélectionné"); return; }
        bulkRef.current = { ids: new Set(chosen.map((d) => d.id)), total: chosen.length, done: 0 };
        setBulkProgress({ done: 0, total: chosen.length });
        setSelected(new Set());
        // Concurrence maîtrisée : lots de 3 (évite d'exploser le navigateur), le poller existant suit chaque task_id.
        for (let i = 0; i < chosen.length; i += 3) {
            const batch = chosen.slice(i, i + 3);
            await Promise.allSettled(batch.map(async (d) => {
                try { await extraireDocumentAsync(d.id); pendingRef.current.add(d.id); setExtracting((x) => ({ ...x, [d.id]: true })); }
                catch (e) { _bulkTick(d.id); toast.error(`Extraction impossible : ${d.nom_fichier}`, { description: e.message }); }
            }));
        }
    }

    const groups = [
        { key: "entite", holding: false, title: "Dataroom — Entité Auditée", icon: FolderOpen, empty: "Aucun document. Déposez les bilans, liasses fiscales et pièces comptables de l'entité auditée." },
        { key: "holding", holding: true, title: "Dataroom — Holding / Groupe", icon: Building2, empty: "Aucun document de groupe. Déposez ici les comptes consolidés, liasses et FEC de la holding." },
    ].filter((g) => !group || g.key === group);

    return (
        <section data-testid="dataroom-section" className="space-y-12">
            {error ? (
                <p data-testid="dataroom-error" className="flex gap-2 text-sm text-destructive"><AlertTriangle className="h-4 w-4 shrink-0" /> {error}</p>
            ) : groups.map((g) => {
                const list = sortDocuments(docs.filter((d) => !!d.est_holding === g.holding && !d.est_livrable && !CASH_RJ_TYPES.has(d.type_document)));
                const Icon = g.icon;
                return (
                    <div key={g.key} data-testid={`dataroom-${g.key}`} className="space-y-6">
                        <div className="flex items-center justify-between">
                            <div className="flex items-center gap-3">
                                <div className="h-9 w-9 rounded-full bg-accent flex items-center justify-center"><Icon className="h-4 w-4 text-primary" strokeWidth={1.75} /></div>
                                <h2 className="font-display text-2xl text-foreground">{g.title}</h2>
                            </div>
                            <span data-testid={`dataroom-count-${g.key}`} className="text-xs uppercase tracking-[0.18em] text-muted-foreground">{list.length} document{list.length > 1 ? "s" : ""}</span>
                        </div>
                        <UploadZone onFiles={(files, type, periode, tp) => handleUpload(files, type, periode, tp, g.holding)} testPrefix={g.key} />
                        {loading ? (
                            <p className="text-sm text-muted-foreground">Chargement…</p>
                        ) : list.length === 0 ? (
                            <p data-testid={`dataroom-empty-${g.key}`} className="text-sm text-muted-foreground">{g.empty}</p>
                        ) : (
                            <div data-testid={`dataroom-list-${g.key}`} className="space-y-5">
                                {categoriesFor(isResto).map((cat) => {
                                    const sub = list.filter((d) => categorize(d) === cat.key);
                                    const CIcon = cat.icon;
                                    return (
                                        <div key={cat.key} data-testid={`dataroom-${g.key}-${cat.key}`} className="rounded-xl border border-slate-200 bg-white shadow-sm">
                                            <div className="flex items-center gap-2 border-b border-slate-200 bg-slate-50 px-5 py-2.5 rounded-t-xl">
                                                <CIcon className="h-4 w-4 text-indigo-600" strokeWidth={1.75} />
                                                <h3 className="text-sm font-semibold text-slate-700">{cat.title}</h3>
                                                <span className="ml-auto text-xs text-slate-500">{sub.length} fichier{sub.length > 1 ? "s" : ""}</span>
                                            </div>
                                            {sub.length === 0 ? <p className="px-5 py-3 text-xs text-slate-400">{cat.empty}</p> : (
                                                <ul className="divide-y divide-slate-100">
                                                    {sub.map((doc) => (
                                                        <DocumentRow key={doc.id} doc={doc} onDownload={handleDownload} onDelete={handleDelete} onExtract={handleExtract} onToggleAnalyse={handleToggleAnalyse} onRemarques={handleRemarques} onRename={handleRename} selected={selected.has(doc.id)} onToggleSelect={toggleSelect} extracting={!!extracting[doc.id]} datePending={!!datePending[doc.id]} />
                                                    ))}
                                                </ul>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </div>
                );
            })}
            {bulkProgress && (
                <div data-testid="dataroom-bulk-progress" className="fixed bottom-24 left-1/2 z-40 -translate-x-1/2 rounded-full bg-indigo-600 px-4 py-2 text-xs text-white shadow-lg flex items-center gap-2">
                    <Loader2 className="h-3.5 w-3.5 animate-spin" /> Extraction IA en cours : {bulkProgress.done} / {bulkProgress.total} fichiers terminés
                </div>
            )}
            {selected.size > 0 && (
                <div data-testid="dataroom-bulk-bar" className="fixed bottom-6 left-1/2 z-50 flex -translate-x-1/2 flex-wrap items-center gap-3 rounded-full border border-slate-200 bg-white px-4 py-2.5 shadow-xl">
                    <span data-testid="dataroom-bulk-count" className="text-sm font-semibold text-slate-700">{selected.size} sélectionné{selected.size > 1 ? "s" : ""}</span>
                    <div className="flex items-center gap-1.5">
                        <select data-testid="dataroom-bulk-move-select" value={moveType} onChange={(e) => setMoveType(e.target.value)} className="h-8 rounded-md border border-slate-300 bg-white px-2 text-xs">
                            <option value="">Déplacer vers…</option>
                            {TYPES_DOCUMENT.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
                        </select>
                        <button type="button" data-testid="dataroom-bulk-move" disabled={!moveType} onClick={bulkMove} className="inline-flex items-center gap-1.5 rounded-full border border-indigo-300 px-3 py-1.5 text-xs font-medium text-indigo-700 hover:bg-indigo-50 disabled:opacity-50"><FolderInput className="h-3.5 w-3.5" /> Déplacer</button>
                    </div>
                    <button type="button" data-testid="dataroom-bulk-extract" onClick={bulkExtract} className="inline-flex items-center gap-1.5 rounded-full border border-indigo-300 px-3 py-1.5 text-xs font-medium text-indigo-700 hover:bg-indigo-50"><ScanText className="h-3.5 w-3.5" /> Extraire le texte (IA)</button>
                    <button type="button" data-testid="dataroom-bulk-delete" onClick={bulkDelete} className="inline-flex items-center gap-1.5 rounded-full border border-rose-300 px-3 py-1.5 text-xs font-medium text-rose-700 hover:bg-rose-50"><Trash2 className="h-3.5 w-3.5" /> Supprimer</button>
                    <button type="button" data-testid="dataroom-bulk-clear" onClick={() => setSelected(new Set())} title="Annuler la sélection" className="h-7 w-7 rounded-full flex items-center justify-center text-slate-400 hover:bg-slate-100"><X className="h-4 w-4" /></button>
                </div>
            )}
        </section>
    );
}
